import {
  ANALYTICS_RELOAD_DELAY_MS,
  analyticsEvent,
  SupportedEvents,
} from "../analytics"
import { FEATURE_FLAGS, HOME_PATHS } from "../constants"
import { useCheckForFeatureFlag } from "./useCheckForFeatureFlag"
import {
  getDaysOnNewMobileAppInterface,
  MOBILE_APP_INTERFACES,
  MobileAppInterface,
  MobileAppInterfaceSwitchSource,
  NewMobileAppNudgeDismissMethod,
} from "@mobile/utils/mobileAppInterface"

import { useFetchFeatureFlagsQuery } from "../redux/api/featureFlags"
import {
  dismissNewMobileAppNudge,
  switchMobileAppInterface,
} from "../redux/app/appSlice"
import { selectMobileAppInterface } from "../redux/app/selectors"
import { useAppSelector } from "../redux/reducers"
import { selectIsAuth } from "../redux/selectors"
import { selectUser } from "../redux/user/selectors"
import { isOfficeManager, isPortalAdmin } from "../redux/user/utils"
import { useActions } from "../redux/utils"

/**
 * Tearing one app tree down and mounting the other in the same document
 * sticks iOS WebKit hit-testing, so the screen that just loaded ignores taps
 * until the app is killed. The choice is already in localStorage, so a fresh
 * document loads the right bundle. assign() does nothing when the URL is
 * already the home screen, which is where the old-app nudge lives.
 */
const reloadMobileAppAtHome = () => {
  if (window.location.pathname === HOME_PATHS.mobile) {
    window.location.reload()
    return
  }

  window.location.assign(HOME_PATHS.mobile)
}

let hasScheduledMobileAppInterfaceSwitch = false

/**
 * JOAN-11527: single place that decides which mobile interface renders.
 * The company-wide `newMobileApp.isEnabled` flag always wins and forces the
 * new app. Otherwise admins and office managers behind the early-access flag
 * may opt in per device; everybody else stays on the old app.
 */
export const useMobileAppInterface = () => {
  const { isSuccess: areFlagsLoaded } = useFetchFeatureFlagsQuery()
  const isNewMobileAppForced = useCheckForFeatureFlag(
    FEATURE_FLAGS.NEW_MOBILE_APP,
  )
  const hasEarlyAccessFlag = useCheckForFeatureFlag(
    FEATURE_FLAGS.NEW_MOBILE_APP_ADMINS_OFFICE_MANAGERS_ACCESS,
  )
  const isAuth = useAppSelector(selectIsAuth)
  const { entry: currentUser, isLoaded: isUserLoaded } =
    useAppSelector(selectUser)
  const { selectedInterface, switchedToNewAt, isNudgeDismissed } =
    useAppSelector(selectMobileAppInterface)

  const actions = useActions({
    switchMobileAppInterface,
    dismissNewMobileAppNudge,
  })

  const switchToMobileApp = (
    toInterface: MobileAppInterface,
    source: MobileAppInterfaceSwitchSource,
  ) => {
    if (hasScheduledMobileAppInterfaceSwitch) {
      return
    }

    hasScheduledMobileAppInterfaceSwitch = true
    const daysOnNewInterface = getDaysOnNewMobileAppInterface(switchedToNewAt)

    actions.switchMobileAppInterface(toInterface)
    analyticsEvent(
      SupportedEvents.APP_INTERFACE_SWITCHED,
      {
        to_interface: toInterface,
        source,
        ...(toInterface === MOBILE_APP_INTERFACES.OLD
          ? { days_on_new_interface: daysOnNewInterface }
          : {}),
        app_version: import.meta.env.VITE_APP_VERSION,
      },
      { sendInstantly: true },
    )
    window.setTimeout(reloadMobileAppAtHome, ANALYTICS_RELOAD_DELAY_MS)
  }

  const dismissNudge = (dismissMethod: NewMobileAppNudgeDismissMethod) => {
    actions.dismissNewMobileAppNudge()
    analyticsEvent(SupportedEvents.NEW_APP_POPUP_DISMISSED, {
      dismiss_method: dismissMethod,
      app_version: import.meta.env.VITE_APP_VERSION,
    })
  }

  const canSwitchMobileAppInterface =
    !isNewMobileAppForced &&
    hasEarlyAccessFlag &&
    (isPortalAdmin(currentUser) || isOfficeManager(currentUser))

  const isNewMobileAppActive =
    isNewMobileAppForced ||
    (canSwitchMobileAppInterface &&
      selectedInterface === MOBILE_APP_INTERFACES.NEW)

  return {
    isNewMobileAppActive,
    isNewMobileAppForced,
    canSwitchMobileAppInterface,
    // Roles come from the user, so the pick is only final once both are in.
    isResolved: areFlagsLoaded && (!isAuth || isUserLoaded),
    // Once an interface was picked the user already knows about the new app.
    shouldShowNewMobileAppNudge:
      canSwitchMobileAppInterface &&
      selectedInterface === null &&
      !isNudgeDismissed,
    switchedToNewAt,
    switchToNewMobileApp: (source: MobileAppInterfaceSwitchSource) => {
      switchToMobileApp(MOBILE_APP_INTERFACES.NEW, source)
    },
    switchToOldMobileApp: (source: MobileAppInterfaceSwitchSource) => {
      switchToMobileApp(MOBILE_APP_INTERFACES.OLD, source)
    },
    dismissNewMobileAppNudge: dismissNudge,
  }
}
